import * as React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import camelCase from 'lodash/camelCase';
import moment from 'moment/moment';
import { useState } from 'react';

import { Row, Col, Input, FormGroup, HelpBlock, ControlLabel } from 'components/bootstrap';
import type { EventProcessorConfig } from 'components/event-definitions/event-definitions-types';
import * as FormsUtils from 'util/FormsUtils';
import { describeExpression } from 'util/CronUtils';
import { TimeUnitInput, TimezoneSelect } from 'components/common';
import { TIME_UNITS } from 'components/event-definitions/event-definition-types/FilterForm';
import type { EventDefinitionValidation } from 'components/event-definitions/types';
import useUserDateTime from 'hooks/useUserDateTime';
import { extractDurationAndUnit } from 'components/common/TimeUnitInput';

type RiskScoreThresholdConfig = {
  type: string;
  threshold: number;
  skip_first_run: boolean;
};

type Config = EventProcessorConfig & {
  config?: RiskScoreThresholdConfig;
};

type Props = {
  eventDefinition: {
    id?: string;
    title: string;
    config: Config;
  };
  onChange: (attribute: string, config: any) => void;
  validation: EventDefinitionValidation;
};

const initialRiskThresholdConfig: RiskScoreThresholdConfig = {
  type: 'risk_score_threshold',
  threshold: 75,
  skip_first_run: false,
};

const AssetEventForm = ({ eventDefinition, onChange, validation }: Props) => {
  const [currentConfig, setCurrentConfig] = useState(eventDefinition.config);
  const subtypeConfig = currentConfig.config || initialRiskThresholdConfig;
  const { execute_every_ms: executeEveryMs, search_within_ms: searchWithinMs } = eventDefinition.config;
  const searchWithin = extractDurationAndUnit(searchWithinMs, TIME_UNITS);
  const executeEvery = extractDurationAndUnit(executeEveryMs, TIME_UNITS);
  const [searchWithinMsUnit, setSearchWithinMsUnit] = useState<string>(searchWithin.unit);
  const [executeEveryMsUnit, setExecuteEveryMsUnit] = useState<string>(executeEvery.unit);
  const [searchWithinMsDuration, setSearchWithinMsDuration] = useState<number>(searchWithin.duration);
  const [executeEveryMsDuration, setExecuteEveryMsDuration] = useState<number>(executeEvery.duration);
  const { userTimezone } = useUserDateTime();
  const [cronDescription, setCronDescription] = useState<string>(
    eventDefinition.config.cron_expression ? describeExpression(eventDefinition.config.cron_expression) : '',
  );

  const setCurrentConfigAndPropagateChange = (config: Config) => {
    setCurrentConfig(config);
    onChange('config', config);
  };

  const handleConfigChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    const config = cloneDeep(eventDefinition.config);
    config[name] = value;
    setCurrentConfigAndPropagateChange(config);
  };

  const handleSubTypeConfigChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    const config = cloneDeep(eventDefinition.config);
    if (!config.config) {
      config.config = subtypeConfig;
    }
    config.config[name] = value;
    setCurrentConfigAndPropagateChange(config);
  };

  const handleTimeRangeChange =
    (fieldName: string) => (nextValue: number, nextUnit: 'hours' | 'minutes' | 'seconds') => {
      const durationInMs = moment.duration(Math.max(nextValue, 1), nextUnit).asMilliseconds();

      const config = cloneDeep(eventDefinition.config);
      config[fieldName] = durationInMs;
      onChange('config', config);

      const stateFieldName = camelCase(fieldName);

      if (stateFieldName === 'searchWithinMs') {
        setSearchWithinMsDuration(nextValue);
        setSearchWithinMsUnit(nextUnit);

        return;
      }

      setExecuteEveryMsDuration(nextValue);
      setExecuteEveryMsUnit(nextUnit);
    };

  const handleCronTimezoneChange = (tz: string) => {
    const config = cloneDeep(eventDefinition.config);
    config.cron_timezone = tz;
    setCurrentConfigAndPropagateChange(config);
  };

  const handleUseCronSchedulingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    const config = cloneDeep(eventDefinition.config);
    config[name] = value;

    if (value) {
      config.cron_expression = '';
      config.cron_timezone = userTimezone;
    } else {
      config.cron_expression = null;
      config.cron_timezone = null;
    }

    setCurrentConfigAndPropagateChange(config);
  };

  return (
    <Row>
      <Col md={7} lg={6}>
        <Input
          id="risk-score-threshold"
          name="threshold"
          label="Risk Score Threshold"
          type="number"
          min={1}
          max={100}
          value={subtypeConfig.threshold}
          onChange={handleSubTypeConfigChange}
          required
        />
        {!eventDefinition.id && (
          <Input
            id="skip-first-run-checkbox"
            type="checkbox"
            name="skip_first_run"
            label="Skip Events on First Run"
            help="Should the first execution skip emitting events for assets already above threshold?"
            checked={subtypeConfig.skip_first_run}
            onChange={handleSubTypeConfigChange}
            disabled={!!eventDefinition.id}
          />
        )}
        {subtypeConfig.type !== 'risk_score_threshold' && (
          <FormGroup controlId="search-within" validationState={validation.errors.search_within_ms ? 'error' : null}>
            <TimeUnitInput
              label="Search within the last"
              update={handleTimeRangeChange('search_within_ms')}
              value={searchWithinMsDuration}
              unit={searchWithinMsUnit}
              units={TIME_UNITS}
              clearable
              required
            />
            {validation?.errors.search_within_ms && <HelpBlock>{validation.errors.search_within_ms[0]}</HelpBlock>}
          </FormGroup>
        )}
        <Input
          id="is-cron-checkbox"
          type="checkbox"
          name="use_cron_scheduling"
          label="Use Cron Scheduling"
          help="Schedule this event with a Quartz cron expression"
          checked={eventDefinition.config.use_cron_scheduling ?? false}
          onChange={handleUseCronSchedulingChange}
        />
        {currentConfig.use_cron_scheduling ? (
          <>
            <FormGroup controlId="cron-expression" validationState={validation.errors.cron_expression ? 'error' : null}>
              <Input
                id="cron-expression"
                name="cron_expression"
                label="Cron Expression"
                type="text"
                help={
                  <span>
                    {cronDescription || 'A Quartz cron expression to determine when the event should be run.'}
                  </span>
                }
                value={currentConfig.cron_expression ?? ''}
                onBlur={() => setCronDescription(describeExpression(currentConfig.cron_expression))}
                onChange={handleConfigChange}
              />
              {validation.errors.cron_expression && <HelpBlock>{validation.errors.cron_expression[0]}</HelpBlock>}
            </FormGroup>
            <FormGroup>
              <ControlLabel>Cron Time Zone</ControlLabel>
              <TimezoneSelect
                value={currentConfig.cron_timezone ?? userTimezone}
                name="cron_timezone"
                clearable={false}
                onChange={handleCronTimezoneChange}
              />
            </FormGroup>
          </>
        ) : (
          <FormGroup controlId="execute-every" validationState={validation.errors.execute_every_ms ? 'error' : null}>
            <TimeUnitInput
              label="Execute search every"
              update={handleTimeRangeChange('execute_every_ms')}
              value={executeEveryMsDuration}
              unit={executeEveryMsUnit}
              units={TIME_UNITS}
              clearable
              required
            />
            {validation.errors.execute_every_ms && <HelpBlock>{validation.errors.execute_every_ms[0]}</HelpBlock>}
          </FormGroup>
        )}
        <Input
          id="schedule-checkbox"
          type="checkbox"
          name="_is_scheduled"
          label="Enable"
          help="Should this event definition be executed automatically?"
          checked={eventDefinition.config._is_scheduled ?? true}
          onChange={handleConfigChange}
        />
      </Col>
    </Row>
  );
};

AssetEventForm.defaultConfig = {
  search_within_ms: 0,
  execute_every_ms: 5 * 60 * 1000,
  _is_scheduled: true,
  use_cron_scheduling: false,
  config: initialRiskThresholdConfig,
} as any;

export default AssetEventForm;
